import { EAnalysisStages, EAnalysisStatus } from "@/src/constants";
import { IAnalysisProgress } from "@/src/lib/database/schema/jobanalysis";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const POLLING_INTERVAL_MS = 3_000; // 3 seconds

export function useJobProgress({
  fetchUpdateURL,
  fetchStartURL,
  startBody,
}: {
  fetchUpdateURL: string | null;
  fetchStartURL: string;
  startBody: any;
}) {
  const [progress, setProgress] = useState<IAnalysisProgress>({
    status: EAnalysisStatus.IDLE,
    progress_percentage: 0,
    message: "",
    error: "",
    stage: EAnalysisStages.VALIDATING,
  });

  const [polling, setPolling] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const fetchProgress = useCallback(async () => {
    if (!fetchUpdateURL) return EAnalysisStatus.IDLE;

    setError(null);

    try {
      const res = await fetch(fetchUpdateURL);
      const result = await res.json();
      if (res.ok && result.success) {
        setData(result.data);
        setProgress(result.data?.progress);
        if(result.data?.progress?.error) {
          setError(result.data.progress?.message);
        }
        return result.data?.progress?.status;
      } else {
        setError(result.error || "Failed to fetch progress");
        return result.data?.progress?.status || EAnalysisStatus.FAILED;
      }
    } catch (err) {
      setError((err as Error).message || "Fetch failed");
      return EAnalysisStatus.FAILED;
    }
  }, [fetchUpdateURL]);

  const startJob = useCallback(async (data?:any) => {
    setError(null);
    setIsLoading(true);

    try {
      const dataBody = data || startBody;
      const res = await fetch(fetchStartURL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(dataBody || {}),
      });

      const result = await res.json();
      setIsLoading(false);

      if (res.ok && result.success) {
        setProgress((p) => ({
          ...p,
          status: EAnalysisStatus.PENDING,
        }));
        setPolling(true);

        if (result.message) {
          toast(result.message, { duration: 3000 });
        }

        return result; // non-opinionated
      } else {
        setError(result.error || "Failed to start");
        return null;
      }
    } catch (err) {
      setIsLoading(false);
      setError((err as Error).message || "Failed to start");
      return null;
    }
  }, [fetchStartURL, startBody]);

  // Check on mount/update if job already started
  useEffect(() => {
    if (!fetchUpdateURL) {
      setIsInitialLoading(false);
      return;
    }

    fetchProgress().then((status) => {
      setIsInitialLoading(false);
      if (
        status === EAnalysisStatus.PENDING ||
        status === EAnalysisStatus.IN_PROGRESS
      ) {
        setPolling(true);
      }
    });
  }, [fetchProgress, fetchUpdateURL]);

  // Polling effect
  useEffect(() => {
    if (!polling) return;

    let interval: NodeJS.Timeout;
    if(fetchUpdateURL) {
        interval = setInterval(async () => {
        const status = await fetchProgress();
        if (
          status === EAnalysisStatus.DONE ||
          status === EAnalysisStatus.FAILED
        ) {
          clearInterval(interval);
          setPolling(false);
        }
      }, POLLING_INTERVAL_MS);
    }

    return () => clearInterval(interval);
  }, [polling, fetchProgress, fetchUpdateURL]);

  const isInProgress = useMemo(()=>{
    return progress?.status === EAnalysisStatus.PENDING || progress?.status === EAnalysisStatus.IN_PROGRESS
  }, [progress]);


  return {
    progress,
    isInProgress,
    isLoading,
    isInitialLoading,
    isPolling: polling,
    data,
    error,
    startJob,
  };
}
